/**
 * @license Licensed under the Apache License, Version 2.0 (the "License"):
 *          http://www.apache.org/licenses/LICENSE-2.0
 */

/**
 * @fileoverview Code generator for Arduino Digital and Analogue input/output.
 *     Arduino built in function docs: http://arduino.cc/en/Reference/HomePage
 */
import * as goog from '../../closure/goog/goog.js';
goog.declareModuleId('Blockly.Arduino.gen_io');

import {arduinoGenerator as Arduino} from '../arduino.js';
import { Hardware } from '../../core/hardware.js';

/**
 * Function for 'set pin' (X) to a state (Y).
 * Arduino code: setup { pinMode(X, OUTPUT); }
 *               loop  { digitalWrite(X, Y); }
 * @param {!Blockly.Block} block Block to generate the code from.
 * @return {string} Completed code.
 */
Arduino['g_io_digitalwrite'] = function(block) {

  var moduleName = block.getFieldValue('ModuleName');
  var ports;

  for (let index = 0; index < Hardware.usedModules.length; index++)
  {
    if (Hardware.usedModules[index][1] == moduleName)
    {
      ports = Hardware.usedModules[index][3];
      break;
    }
  }

  var pin = ports[0]; 

  var stateOutput = Arduino.valueToCode(
      block, 'STATE', Arduino.ORDER_ATOMIC) || 'LOW';

  Arduino.reservePin(
      block, pin, Arduino.PinTypes.OUTPUT, 'Digital Write');

  var pinSetupCode = 'pinMode(' + pin + ', OUTPUT);';
  Arduino.addSetup('io_' + pin, pinSetupCode, false);

  var code = 'digitalWrite(' + pin + ', ' + stateOutput + ');\n';
  return code;
};

/**
 * Function for reading a digital pin (X).
 * Arduino code: setup { pinMode(X, INPUT); }
 *               loop  { digitalRead(X)     }
 * @param {!Blockly.Block} block Block to generate the code from.
 * @return {array} Completed code with order of operation.
 */
Arduino['g_io_digitalread'] = function(block) {

  var moduleName = block.getFieldValue('ModuleName');
  var ports;

  for (let index = 0; index < Hardware.usedModules.length; index++)
  {
    if (Hardware.usedModules[index][1] == moduleName)
    {
      ports = Hardware.usedModules[index][3];
      break;
    }
  }

  var pin = ports[0]; 

  Arduino.reservePin(
      block, pin, Arduino.PinTypes.INPUT, 'Digital Read');

  var pinSetupCode = 'pinMode(' + pin + ', INPUT);';
  Arduino.addSetup('io_' + pin, pinSetupCode, false);

  var code = 'digitalRead(' + pin + ')';
  return [code, Arduino.ORDER_ATOMIC];
};

/**
 * Function for setting the state (Y) of an analogue output (X).
 * Arduino code: setup { pinMode(X, OUTPUT); }
 *               loop  { analogWrite(X, Y);  }
 * @param {!Blockly.Block} block Block to generate the code from.
 * @return {string} Completed code.
 */
Arduino['g_io_analogwrite'] = function(block) {

  var moduleName = block.getFieldValue('ModuleName');
  var ports;

  for (let index = 0; index < Hardware.usedModules.length; index++)
  {
    if (Hardware.usedModules[index][1] == moduleName)
    {
      ports = Hardware.usedModules[index][3];
      break;
    }
  }
  var pin = ports[0]; 

  var stateOutput = Arduino.valueToCode(
      block, 'NUM', Arduino.ORDER_ATOMIC) || '0';

  Arduino.reservePin(
      block, pin, Arduino.PinTypes.OUTPUT, 'Analogue Write');

  var pinSetupCode = 'pinMode(' + pin + ', OUTPUT);';
  Arduino.addSetup('io_' + pin, pinSetupCode, false);

  // Warn if the input value is out of range
  if ((stateOutput < 0) || (stateOutput > 255)) {
    block.setWarningText('The analogue value set must be between 0 and 255',
                         'pwm_value');
  } else {
    block.setWarningText(null, 'pwm_value');
  }

  var code = 'analogWrite(' + pin + ', ' + stateOutput + ');\n';
  return code;
};

/**
 * Function for reading an analogue pin value (X).
 * Arduino code: setup { pinMode(X, INPUT); }
 *               loop  { analogRead(X)      }
 * @param {!Blockly.Block} block Block to generate the code from.
 * @return {array} Completed code with order of operation.
 */
Arduino['g_io_analogread'] = function(block) {
  var moduleName = block.getFieldValue('ModuleName');
  var ports;

  for (let index = 0; index < Hardware.usedModules.length; index++)
  {
    if (Hardware.usedModules[index][1] == moduleName)
    {
      ports = Hardware.usedModules[index][3];
      break;
    }
  }
  var pin = ports[0]; 

  Arduino.reservePin(
      block, pin, Arduino.PinTypes.INPUT, 'Analogue Read');

  var pinSetupCode = 'pinMode(' + pin + ', INPUT);';
  Arduino.addSetup('io_' + pin, pinSetupCode, false);

  var code = 'analogRead(' + pin + ')';
  return [code, Arduino.ORDER_ATOMIC];
};

Arduino['g_io_pulsein'] = function(block) {
  var moduleName = block.getFieldValue('ModuleName');
  var ports;

  for (let index = 0; index < Hardware.usedModules.length; index++)
  {
    if (Hardware.usedModules[index][1] == moduleName)
    {
      ports = Hardware.usedModules[index][3];
      break;
    }
  }
  var pin = ports[0]; 

  var type = Arduino.valueToCode(block, "PULSETYPE", Arduino.ORDER_ATOMIC);

  Arduino.reservePin(
      block, pin, Arduino.PinTypes.INPUT, 'Pulse Pin');

  var pinSetupCode = 'pinMode(' + pin + ', INPUT);\n';
  Arduino.addSetup('io_' + pin, pinSetupCode, false);

  var code = 'pulseIn(' + pin + ', ' + type + ')';

  return [code, Arduino.ORDER_ATOMIC];
};

Arduino['g_io_pulsetimeout'] = function(block) {
  var moduleName = block.getFieldValue('ModuleName');
  var ports;

  for (let index = 0; index < Hardware.usedModules.length; index++)
  {
    if (Hardware.usedModules[index][1] == moduleName)
    {
      ports = Hardware.usedModules[index][3];
      break;
    }
  }
  var pin = ports[0]; 

  var type = Arduino.valueToCode(block, "PULSETYPE", Arduino.ORDER_ATOMIC);
  var timeout = Arduino.valueToCode(block, "TIMEOUT", Arduino.ORDER_ATOMIC);

  Arduino.reservePin(
      block, pin, Arduino.PinTypes.INPUT, 'Pulse Pin');

  var pinSetupCode = 'pinMode(' + pin + ', INPUT);\n';
  Arduino.addSetup('io_' + pin, pinSetupCode, false);

  var code = 'pulseIn(' + pin + ', ' + type + ', ' + timeout + ')';

  return [code, Arduino.ORDER_ATOMIC];
}; 
